import { useCallback } from 'react';
import { endpoints, type CalendarEvent, type FeedItem } from '../services/api';
import { fetchForecast, type Forecast } from '../services/weather';
import { useSettings, type CalendarSource } from '../store/settings';
import { usePolling, type PollState } from './usePolling';

export function useWeather(): PollState<Forecast> {
  const { enabled, location, unit } = useSettings((s) => s.weather);
  const key = enabled && location ? `${location.latitude},${location.longitude},${unit}` : null;
  const load = useCallback((signal: AbortSignal) => fetchForecast(location!, unit, signal), [location, unit]);
  return usePolling(key, load, 30 * 60_000);
}

export interface NewsItem extends FeedItem {
  source: string;
}

export function useNews(): PollState<NewsItem[]> {
  const { enabled, sources } = useSettings((s) => s.news);
  const active = sources.filter((s) => s.enabled);
  const key = enabled && active.length ? active.map((s) => s.url).join('|') : null;
  const load = useCallback(async () => {
    const results = await Promise.allSettled(active.map((s) => endpoints.rss(s.url).then((f) => ({ s, f }))));
    const items = results.flatMap((r) =>
      r.status === 'fulfilled' ? r.value.f.items.slice(0, 12).map((i) => ({ ...i, source: r.value.s.name })) : [],
    );
    if (!items.length && results.some((r) => r.status === 'rejected')) throw new Error('News feeds unavailable');
    // Interleave sources by recency.
    return items.sort((a, b) => (b.published ?? '').localeCompare(a.published ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return usePolling(key, load, 15 * 60_000);
}

export interface AgendaEvent extends CalendarEvent {
  calendar: Pick<CalendarSource, 'name' | 'color'>;
}

export function useCalendar(): PollState<AgendaEvent[]> {
  const { enabled, sources, days } = useSettings((s) => s.calendar);
  const active = sources.filter((s) => s.enabled);
  const key = enabled && active.length ? `${days}|${active.map((s) => s.url + s.color).join('|')}` : null;
  const load = useCallback(async () => {
    const results = await Promise.allSettled(active.map((s) => endpoints.ics(s.url, days).then((r) => ({ s, r }))));
    const events = results.flatMap((res) =>
      res.status === 'fulfilled'
        ? res.value.r.events.map((e) => ({ ...e, calendar: { name: res.value.s.name, color: res.value.s.color } }))
        : [],
    );
    if (!events.length && results.some((r) => r.status === 'rejected')) throw new Error('Calendars unavailable');
    return events.sort((a, b) => a.start.localeCompare(b.start));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return usePolling(key, load, 10 * 60_000);
}
